import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from '../../utils/api';
import { formatMotion } from '../../utils/format-helpers';
import { Button } from 'src/components/ui/button';
import { Card } from 'src/components/ui/card';
import { Slider } from 'src/components/ui/slider';
import { toast } from 'react-hot-toast';
import { Award, Star, Check, RefreshCw } from 'lucide-react';
import RatingEventBus from '../../services/RatingEventBus';

interface DebaterViewProps {
  roundData: any;
  userRole: string;
  roundId: string;
}

const DebaterView: React.FC<DebaterViewProps> = ({ roundData, userRole, roundId }) => {
  const [adjudicatorRatings, setAdjudicatorRatings] = useState<{ [key: number]: number }>({});
  const [submitting, setSubmitting] = useState(false);
  const [ratingsModified, setRatingsModified] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Add component ID for tracking
  const componentId = `debater-view-${roundId}`;

  // Register the component on mount
  useEffect(() => {
    RatingEventBus.registerComponent(componentId);

    return () => {
      RatingEventBus.unregisterComponent(componentId);
    };
  }, [componentId]);

  // Check submission status on component mount
  useEffect(() => {
    const checkSubmissionStatus = async () => {
      try {
        const response = await fetchWithAuth(`/api/round/${roundId}/rating-status/`);

        // Find current user's ID
        const currentUserId = roundData.current_user_id;

        // Look for the current user in the participants list
        const allParticipants = [
          ...(response.participants?.debaters || []),
          ...(response.participants?.adjudicators || []),
          ...(response.participants?.spectators || [])
        ];

        const currentUserData = allParticipants.find(p => p.user_id === currentUserId);

        // If the user has submitted ratings, update the state
        if (currentUserData?.has_submitted_ratings) {
          setHasSubmitted(true);
        }
      } catch (err) {
        console.error("Error checking submission status:", err);
        // Don't update hasSubmitted state if there's an error
      }
    };

    // Check submission status from sessionStorage first (faster)
    const savedSubmissionState = sessionStorage.getItem(`debater-${roundId}-submitted`);

    if (savedSubmissionState === 'true') {
      setHasSubmitted(true);
    } else {
      // Only check submission status if not already marked as submitted
      checkSubmissionStatus();
    }
  }, [roundId, roundData.current_user_id]);

  // Get team info based on role
  const getTeamFromRole = () => {
    if (['Prime Minister', 'Deputy Prime Minister', 'Government Whip', 'Member of Government'].includes(userRole)) {
      return 'Government';
    } else if (['Leader of Opposition', 'Deputy Leader of Opposition', 'Opposition Whip', 'Member of Opposition'].includes(userRole)) {
      return 'Opposition';
    }
    return '';
  };

  const teamColor = getTeamFromRole() === 'Government' ? 'blue' : 'red';

  // Filter for adjudicators that can be rated (chair and panelists)
  const getRateableAdjudicators = () => {
    return roundData.participants.filter((p: any) =>
      ['Chair Adjudicator', 'Panelist'].includes(p.role)
    );
  };

  // Handle rating change
  const handleRatingChange = (adjudicatorId: number, value: number[]) => {
    setAdjudicatorRatings({
      ...adjudicatorRatings,
      [adjudicatorId]: value[0]
    });
    setRatingsModified(true);
  };

  // Submit ratings
  const submitRatings = async () => {
    try {
      setSubmitting(true);
      
      // Create ratings array from the state
      const ratings = Object.entries(adjudicatorRatings).map(([adjudicatorId, rating]) => {
        // Get the actual adjudicator object from the round data
        const adjudicator = getRateableAdjudicators().find((a: any) => a.id === parseInt(adjudicatorId));
        return {
          adjudicator_id: adjudicator?.user?.id, // Use the actual user ID, not the allocation ID
          rating: rating
        };
      }).filter(item => item.adjudicator_id); // Filter out any undefined adjudicator_ids
      
      if (ratings.length === 0) {
        toast.error('Please provide at least one rating');
        setSubmitting(false);
        return;
      }
      
      // Mark submission in EventBus immediately to update UI
      RatingEventBus.markUserSubmitted(
        roundId, 
        roundData.current_user_id, 
        roundData.current_username, 
        userRole
      );
      
      console.log(`Submitting adjudicator ratings:`, ratings);
      
      // Ensure proper content type is set
      const response = await fetchWithAuth(`/api/round/${roundId}/submit-adjudicator-ratings/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ratings })
      });
      
      // Store submission state
      sessionStorage.setItem(`debater-${roundId}-submitted`, 'true');
      setHasSubmitted(true);
      
      toast.success('Ratings submitted successfully');
      setRatingsModified(false);
      
      // Request manual refresh
      RatingEventBus.publish('MANUAL_REFRESH_REQUESTED', { 
        roundId, 
        timestamp: new Date().toISOString() 
      });
      
    } catch (error) {
      console.error("Error submitting ratings:", error);
      toast.error(`Error submitting ratings: ${error}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Add a warning effect for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (ratingsModified) {
        const message = 'You have unsaved ratings. Are you sure you want to leave?';
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [ratingsModified]);

  return (
    <div className="space-y-6">
      <div className={`p-4 border-2 border-${teamColor}-500 bg-${teamColor}-900 bg-opacity-10 rounded-lg`}>
        <h3 className="font-semibold mb-2">Your Position</h3>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <p className="text-sm text-gray-400">Role</p>
            <p className="font-medium">{userRole}</p>
          </div>
          <div className="flex-1">
            <p className="text-sm text-gray-400">Team</p>
            <p className={`font-medium text-${teamColor}-500`}>{getTeamFromRole()}</p>
          </div>
        </div>
      </div>

      <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <h3 className="text-lg font-semibold mb-2">Motion</h3>
        <p className="dark:text-white light:text-gray-800">{formatMotion(roundData.motion)}</p>
      </div>

      <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-lg">
        <h3 className="font-semibold mb-4 flex items-center">
          <Award className="mr-2" size={18} />
          Rate Adjudicators
        </h3>

        <Button
          onClick={() => {
            RatingEventBus.publish('MANUAL_REFRESH_REQUESTED', {
              roundId,
              timestamp: new Date().toISOString()
            });

            toast.success('Refreshing status...', {
              icon: '🔄',
              duration: 2000
            });
          }}
          variant="outline"
          size="sm"
          className="mb-4"
        >
          <RefreshCw size={14} className="mr-2" />
          Refresh Status
        </Button>

        <div className="space-y-4">
          {!hasSubmitted ? (
            <>
              {getRateableAdjudicators().length > 0 ? (
                <>
                  {getRateableAdjudicators().map((adj: any) => (
                    <Card key={adj.id} className="p-3 border">
                      <div className="flex flex-col md:flex-row md:items-center gap-4">
                        <div className="flex-1">
                          <p className="font-medium">{adj.user.username}</p>
                          <p className="text-xs text-gray-400">{adj.role}</p>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Star className="text-amber-500" size={16} />
                            <p className="text-sm">Rating: {adjudicatorRatings[adj.id] || 1}</p>
                          </div>
                          <Slider
                            defaultValue={[1]}
                            min={1}
                            max={10}
                            step={1}
                            onValueChange={(value) => handleRatingChange(adj.id, value)}
                          />
                          <div className="flex justify-between mt-1">
                            <span className="text-xs text-gray-400">Poor (1)</span>
                            <span className="text-xs text-gray-400">Excellent (10)</span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}

                  <Button
                    onClick={submitRatings}
                    disabled={submitting || Object.keys(adjudicatorRatings).length === 0}
                    className="mt-4"
                  >
                    {submitting ? 'Submitting...' : 'Submit Ratings'}
                  </Button>

                  {ratingsModified && (
                    <div className="mt-2 text-amber-500 text-sm">
                      You have unsaved ratings. Please submit to save your changes.
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-400">No adjudicators available for rating.</p>
              )}
            </>
          ) : (
            <div className="text-center p-4 bg-green-50 dark:bg-green-900 dark:bg-opacity-20 rounded-lg">
              <Check size={32} className="mx-auto mb-2 text-green-500" />
              <h4 className="font-medium text-green-700 dark:text-green-400">Ratings Submitted</h4>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Your adjudicator ratings have been successfully submitted.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DebaterView;